import React from 'react'
import { useMutation, gql } from '@apollo/client'
import { Row, Col, Input, Upload, Button, Image } from 'antd'
import {
  UploadOutlined,
  MinusCircleTwoTone,
  CaretUpFilled,
  CaretDownFilled,
} from '@ant-design/icons'
import * as _ from 'lodash'
import { useTranslation } from 'react-i18next'
import BlockElement from '../../../../components/BlockElement'
import { minusIconStyle, arrowIconStyle } from '../styles'
import { switchElements, disabledMove } from '../utils'
import TextContentEditor from './TextContentEditor'
import { ErrorMessage } from 'formik'

const UPLOAD_FICHE_IMAGE = gql`
  mutation uploadFicheImage($id: Int, $image: Upload!, $ficheComponentId: Int) {
    uploadFicheImage(
      id: $id
      image: $image
      ficheComponentId: $ficheComponentId
    ) {
      ficheImage {
        id
        url
      }
    }
  }
`

const REMOVE_FICHE_IMAGE = gql`
  mutation removeFicheImage($id: Int!) {
    removeFicheImage(id: $id) {
      success
    }
  }
`

interface Props {
  setArticles: (value: any) => void
  articles: [any]
  index: number
}

const Article = (props: Props): JSX.Element => {
  const { t } = useTranslation()
  const [uploadFicheImage] = useMutation(UPLOAD_FICHE_IMAGE)
  const [removeImage] = useMutation(REMOVE_FICHE_IMAGE)
  const { index, articles, setArticles } = props

  const article = articles[index]

  const setArticle = (values: any) => {
    const updatedArticles = [...articles]
    const article = _.cloneDeep(updatedArticles[index])
    updatedArticles[index] = { ...article, ...values }

    setArticles(() => [...updatedArticles])
  }

  const removeArticle = () => {
    const newArticles = _.cloneDeep(articles)
    newArticles.splice(index, 1)
    setArticles(newArticles)
  }

  const reorderArticles = (move: 'up' | 'down') => {
    const newArticles = [...props.articles]
    if (disabledMove(newArticles, article, move)) return
    switchElements(newArticles, article, move)
    setArticles(newArticles)
  }

  const upload = (e: any): Promise<string | void | ArrayBuffer | null> => {
    const file = e.file
    return uploadFicheImage({
      variables: { image: file, ficheComponentId: props.articles[index].id },
    }).then(
      ({
        data: {
          uploadFicheImage: { ficheImage },
        },
      }) => {
        setArticle({
          image_id: ficheImage.id,
          image_url: ficheImage.url,
          image_name: file.name,
        })
      }
    )
  }

  const onRemoveImage = (file: any): Promise<void> => {
    return removeImage({
      variables: { id: file.id },
    }).then(() => {
      setArticle({
        image_id: null,
        image_url: null,
        image_name: null,
      })
    })
  }

  const imageLoaded: any = {
    uid: 'none',
    status: 'done',
    name: article['image_name'],
    url: article['image_url'],
    id: article['image_id'],
  }

  const fileList = article['image_url'] ? [imageLoaded] : []

  return (
    <>
      <Row justify="end">
        <Col span={1}>
          <Row gutter={[6, 0]}>
            <Col>
              <Row>
                <CaretUpFilled
                  style={arrowIconStyle(
                    disabledMove(articles, articles[index], 'up')
                  )}
                  onMouseDown={() => reorderArticles('up')}
                />
              </Row>
              <Row>
                <CaretDownFilled
                  style={arrowIconStyle(
                    disabledMove(articles, articles[index], 'down')
                  )}
                  onMouseDown={() => reorderArticles('down')}
                />
              </Row>
            </Col>
            <Col>
              <MinusCircleTwoTone
                style={minusIconStyle}
                onMouseDown={removeArticle}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <BlockElement>
        <Row gutter={[5, 20]}>
          <Col span={24}>
            <label htmlFor={`articles.${index}.text_title`}>
              {t('ficheComparative.illustratedTexts.labels.textTitle')}
            </label>
            <Input
              name={`articles.${index}.text_title`}
              placeholder={t(
                'ficheComparative.illustratedTexts.placeholders.textTitle'
              )}
              onBlur={e => setArticle({ text_title: e.target.value })}
              defaultValue={props.articles?.[index]?.text_title}
            />
          </Col>
          <Col span={24}>
            <Row gutter={[0, 2]}>
              <Col span={24}>
                <label htmlFor={`articles.${index}.text_content`}>
                  {t('ficheComparative.illustratedTexts.labels.textContent')}
                </label>
                <TextContentEditor element={article} setElement={setArticle} />
                <ErrorMessage
                  name={`articles.${index}.title`}
                  component="div"
                  className="field-error"
                />
              </Col>
            </Row>
          </Col>
          <Col span={4}>
            <Image width={200} src={article['image_url'] || ''} />
          </Col>
          <Col span={4}>
            <Upload
              customRequest={upload}
              onRemove={onRemoveImage}
              fileList={fileList}
            >
              {!article['image_url'] && (
                <Button icon={<UploadOutlined />}>Upload</Button>
              )}
            </Upload>
          </Col>
          <Col span={16}>
            <Row gutter={[5, 5]}>
              <label htmlFor={`articles.${index}.image_alt`}>
                {t('ficheComparative.illustratedTexts.labels.imageAlt')}
              </label>
            </Row>
            <Row gutter={[5, 5]}>
              <Input
                onBlur={e => setArticle({ image_alt: e.target.value })}
                defaultValue={props.articles?.[index]?.image_alt}
                placeholder={t(
                  'ficheComparative.illustratedTexts.placeholders.imageAlt'
                )}
              />
            </Row>
          </Col>
        </Row>
      </BlockElement>
    </>
  )
}

export default Article
